import {
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  FolderAddOutlined,
  MailOutlined,
  PlusCircleOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { Table, Tag, Space, Tooltip, Pagination, Avatar, Popover } from "antd";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import RemovePositionUserApi from "../../api/positions/RemovePositionUserApi";
import DeleteUserApi from "../../api/users/DeleteUserApi";
import DisableUserApi from "../../api/users/DisableUserApi";
import EnableUserApi from "../../api/users/EnableUserApi";
import ResendInviteApi from "../../api/users/ResendInviteApi";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../../helpers/NotificationHelpers";
import { permissionAbility } from "../../helpers/permissionHelpers";
import AddUserPositionModal from "../modal/add/AddUserPositionModal";
import ConfirmationModal from "../modal/ConfirmationModal";
import EditUserModal from "../modal/edit/EditUserModal";
import HardDeleteModal from "../modal/HardDeleteModal";
import InfoUserModal from "../modal/InfoUserModal";
import { LIMIT_PER_PAGE } from "../../helpers/constants";

function UsersTable(props) {
  const [infoModal, setInfoModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [addPosition, setAddPosition] = useState(false);
  const [removePosition, setRemovePosition] = useState(false);
  const [removePositionId, setRemovePositionId] = useState("");
  const [selectedUser, setSelectedUser] = useState();
  const [disableModal, setDisableModal] = useState(false);
  const [enableModal, setEnableModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [resendModal, setResendModal] = useState(false);
  const [data, setData] = useState();
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const positionState = useSelector((state) => state.positionState.value);
  const userLevel = positionState?.userRole?.name;

  useEffect(() => {
    setLoading(props.loading);
    setData(props.data);
  }, [props.loading]);

  /*If page changed from parent component */
  useEffect(() => {
    setPage(props.page);
  }, [props.page]);

  async function deleteUser() {
    const apiResponse = await DeleteUserApi(selectedUser?.id);
    if (apiResponse.error) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.success) {
      openNotificationSuccess("bottomRight", apiResponse.message);
      if (data?.users?.length === 1) {
        props.changePage(page - 1);
      } else {
        props.changePage(page);
      }
    } else {
      openNotificationError("bottomRight", "Couldn't delete. Try again later.");
    }
    setSelectedUser();
    setDeleteModal(false);
  }

  async function disableUser() {
    const apiResponse = await DisableUserApi(selectedUser?.id);
    if (apiResponse.error) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.success) {
      openNotificationSuccess("bottomRight", apiResponse.message);
      props.changePage(page);
    } else {
      openNotificationError(
        "bottomRight",
        "Couldn't disable. Try again later."
      );
    }
    setSelectedUser();
    setDisableModal(false);
  }

  async function enableUser() {
    const apiResponse = await EnableUserApi(selectedUser?.id);
    if (apiResponse.error) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.success) {
      openNotificationSuccess("bottomRight", apiResponse.message);
      props.changePage(page);
    } else {
      openNotificationError("bottomRight", "Couldn't enable. Try again later.");
    }
    setSelectedUser();
    setEnableModal(false);
  }

  async function resendInvite() {
    const apiResponse = await ResendInviteApi(selectedUser?.id);
    if (apiResponse.statusCode) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.success) {
      openNotificationSuccess("bottomRight", "Invitation sent");
      props.changePage(page);
    } else {
      openNotificationError("bottomRight", "Couldn't send. Try again later.");
    }
    setSelectedUser();
    setResendModal(false);
  }

  async function removeUserPosition() {
    const apiResponse = await RemovePositionUserApi(removePositionId);
    if (apiResponse.statusCode) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.success) {
      openNotificationSuccess("bottomRight", "User removed from position");
      props.changePage(page);
    } else {
      openNotificationError("bottomRight", "Couldn't remove. Try again later.");
    }
    setSelectedUser();
    setRemovePositionId("");
    setResendModal(false);
  }

  function statusColor(status) {
    let output = "green";
    if (status === "Invited") {
      output = "orange";
    } else if (status === "Disabled") {
      output = "red";
    }
    return output;
  }

  const columns = [
    {
      title: "Emp ID",
      dataIndex: "empId",
      key: "empId",
      render: (text, record) => <div>{record.employeeId || "-"}</div>,
    },
    {
      title: "Name",
      dataIndex: "firstName",
      key: "firstName",
      render: (text, record) => (
        <>
          {record.firstName ? (
            <div className="flex items-center">
              {record.imageUrl ? (
                <Avatar src={record.imageUrl} />
              ) : (
                <Avatar>{record.firstName?.charAt(0).toUpperCase()}</Avatar>
              )}
              <div className="mx-1">
                <div className="max-w-xs truncate overflow-hidden">
                  {record.firstName + " " + record.lastName}
                </div>
              </div>
            </div>
          ) : (
            "-"
          )}
        </>
      ),
    },
    {
      title: "Username",
      dataIndex: "username",
      key: "username",
      render: (text, record) => <div>{record.username || "-"}</div>,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (text, record) => <div>{record.email || "-"}</div>,
    },
    {
      title: "Positions",
      dataIndex: "positions",
      key: "positions",
      render: (text, record) => (
        <div className="flex">
          <div className="mr-1">{record.positions?.length}</div>
          {permissionAbility(userLevel, "positions", "add") && (
            <Tooltip title={"Add position"}>
              <div
                onClick={(event) => {
                  event.stopPropagation();
                  setAddPosition(true);
                  setSelectedUser(record);
                }}
                className="text-blue-500 hover:text-blue-600 cursor-pointer inline"
              >
                <PlusCircleOutlined />
              </div>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: "Status",
      key: "status",
      dataIndex: "status",
      render: (text) => (
        <Tag color={statusColor(text)}>{text?.toUpperCase()}</Tag>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (text, record) => (
        <Space size="middle">
          {permissionAbility(userLevel, "users", "edit") && (
            <Tooltip title={"Edit email"}>
              <div
                onClick={(event) => {
                  event.stopPropagation();
                  setEditModal(true);
                  setSelectedUser(record);
                }}
                className="text-blue-500 hover:text-blue-600 cursor-pointer"
              >
                <EditOutlined />
              </div>
            </Tooltip>
          )}
          {permissionAbility(userLevel, "users", "add") &&
            record.status === "Invited" && (
              <Tooltip title={"Resend Invite"}>
                <div
                  onClick={(event) => {
                    event.stopPropagation();
                    setResendModal(true);
                    setSelectedUser(record);
                  }}
                  className="text-orange-500 hover:text-orange-600 cursor-pointer"
                >
                  <MailOutlined />
                </div>
              </Tooltip>
            )}
          {permissionAbility(userLevel, "users", "edit") &&
            record.status === "Active" && (
              <Tooltip title={"Disable"}>
                <div
                  onClick={(event) => {
                    event.stopPropagation();
                    setDisableModal(true);
                    setSelectedUser(record);
                  }}
                  className="text-orange-500 hover:text-orange-600 cursor-pointer"
                >
                  <StopOutlined />
                </div>
              </Tooltip>
            )}
          {permissionAbility(userLevel, "users", "edit") &&
            record.status === "Disabled" && (
              <Tooltip title={"Enable"}>
                <div
                  onClick={(event) => {
                    event.stopPropagation();
                    setEnableModal(true);
                    setSelectedUser(record);
                  }}
                  className="text-green-500 hover:text-green-600 cursor-pointer"
                >
                  <CheckCircleOutlined />
                </div>
              </Tooltip>
            )}
          {permissionAbility(userLevel, "users", "delete") &&
            (record.canDelete ? (
              <Tooltip title={"Delete"}>
                <div
                  onClick={(event) => {
                    event.stopPropagation();
                    setDeleteModal(true);
                    setSelectedUser(record);
                  }}
                  className="text-red-500 hover:text-red-600 cursor-pointer"
                >
                  <DeleteOutlined />
                </div>
              </Tooltip>
            ) : record.canArchive ? (
              <Tooltip title={"Archive"}>
                <div
                  onClick={(event) => {
                    event.stopPropagation();
                    setDeleteModal("archive");
                    setSelectedUser(record);
                  }}
                  className="text-red-500 hover:text-red-600 cursor-pointer"
                >
                  <FolderAddOutlined />
                </div>
              </Tooltip>
            ) : (
              <Popover
                placement="left"
                content={" Can't delete this user. This user has positions."}
                trigger="hover"
              >
                <div className="text-gray-500">
                  <FolderAddOutlined />
                </div>
              </Popover>
            ))}
        </Space>
      ),
    },
  ];

  return (
    <>
      <Table
        columns={columns}
        dataSource={data?.users}
        loading={loading}
        pagination={false}
        onRow={(record) => {
          return {
            onClick: () => {
              setInfoModal(true);
              setSelectedUser(record);
            },
          };
        }}
        rowClassName={"cursor-pointer hover:shadow"}
      />
      <div className="text-center mt-6">
        <Pagination
          onChange={(num) => {
            props.changePage(num);
            setPage(num);
          }}
          current={page}
          total={data?.totalCount || 0}
          pageSize={LIMIT_PER_PAGE}
        />
      </div>
      <ConfirmationModal
        visible={disableModal}
        handleCancel={() => {
          setDisableModal(false);
          setSelectedUser();
        }}
        handleOk={() => disableUser()}
        text="Are you sure want to disable this user?"
        title="Disable user"
      />
      <ConfirmationModal
        visible={enableModal}
        handleCancel={() => {
          setEnableModal(false);
          setSelectedUser();
        }}
        handleOk={() => enableUser()}
        text="Are you sure want to enable this user?"
        title="Enable user"
      />
      <ConfirmationModal
        visible={resendModal}
        handleCancel={() => {
          setResendModal(false);
          setSelectedUser();
        }}
        handleOk={() => resendInvite()}
        text="Are you sure want to resend this invitation?"
        title="Resend Invite"
      />
      <HardDeleteModal
        visible={deleteModal}
        handleCancel={() => {
          setDeleteModal(false);
          setSelectedUser();
        }}
        handleOk={() => deleteUser()}
        text="Are you sure want to delete this user?"
        title="Delete user"
      />
      <ConfirmationModal
        visible={removePosition}
        handleCancel={() => {
          setRemovePosition(false);
          setSelectedUser();
          setRemovePositionId("");
        }}
        handleOk={() => removeUserPosition()}
        text="Are you sure want to remove this Position?"
        title="Remove position"
      />
      <EditUserModal
        visible={editModal}
        handleOk={() => props.changePage(page)}
        handleCancel={() => {
          setEditModal(false);
          setSelectedUser();
        }}
        user={selectedUser}
      />
      <InfoUserModal
        visible={infoModal}
        handleCancel={() => {
          setInfoModal(false);
          setSelectedUser();
        }}
        user={selectedUser}
        handlePositionDelete={(positionId) => {
          setInfoModal(false);
          setRemovePosition(true);
          setRemovePositionId(positionId);
        }}
      />
      <AddUserPositionModal
        visible={addPosition}
        handleCancel={() => {
          setAddPosition(false);
          setSelectedUser();
        }}
        handleOk={() => props.changePage(page)}
        user={selectedUser}
      />
    </>
  );
}

export default UsersTable;
